import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { fmtDop } from "../helpers";

type Props = { lang: "es" | "en"; toast: (m: string) => void };

type CohortRow = {
  cohort_month: string;
  cohort_size: number;
  row: Record<string, { users: number; revenue: number }>;
};

type AnalyticsPayload = {
  from: string;
  to: string;
  summary: { orders: number; revenue: number; aov: number; unique_buyers: number };
  ltv: { avg_ltv: number; avg_orders_per_buyer: number; total_buyers: number };
  revenue_daily: Array<{ day: string; revenue: number; orders: number }>;
  top_skus: Array<{ product_id: string; name: string | null; units: number; revenue: number }>;
  cohorts: CohortRow[];
};

const PRESETS = [
  { id: "7d", days: 7, label_es: "7 días", label_en: "7 days" },
  { id: "30d", days: 30, label_es: "30 días", label_en: "30 days" },
  { id: "90d", days: 90, label_es: "90 días", label_en: "90 days" },
  { id: "365d", days: 365, label_es: "1 año", label_en: "1 year" },
];

// Revenue / AOV / LTV / CAC come from `get_business_analytics`, which sums
// order totals already stored in DOP. Render raw via `fmtDop` — no conversion.
const fmt$ = (n: number) => fmtDop(n);

export function AdminAnalyticsDashboard({ lang, toast }: Props) {
  const [days, setDays] = useState(30);
  const [marketingSpend, setMarketingSpend] = useState<string>("");
  const [data, setData] = useState<AnalyticsPayload | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const _from = new Date(Date.now() - days * 86400_000).toISOString();
    supabase
      .rpc("get_business_analytics", { _from, _to: new Date().toISOString() })
      .then(({ data, error }) => {
        if (!alive) return;
        if (error) {
          toast(lang === "es" ? "Error al cargar analítica" : "Failed to load analytics");
          setData(null);
        } else {
          setData(data as AnalyticsPayload);
        }
        setLoading(false);
      });
    return () => { alive = false; };
  }, [days, lang, toast]);

  const cac = useMemo(() => {
    const spend = Number(marketingSpend);
    const buyers = data?.summary?.unique_buyers || 0;
    if (!spend || !buyers) return null;
    return spend / buyers;
  }, [marketingSpend, data]);

  const maxRev = useMemo(
    () => Math.max(1, ...(data?.revenue_daily ?? []).map(d => Number(d.revenue) || 0)),
    [data]
  );

  const cohortOffsets = [0, 1, 2, 3, 4, 5];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="ph">
        <h3>{lang === "es" ? "Dashboard analítico" : "Analytics dashboard"}</h3>
        <p>{lang === "es" ? "AOV, LTV, CAC, cohortes y top SKUs" : "AOV, LTV, CAC, cohorts and top SKUs"}</p>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {PRESETS.map(p => (
          <button key={p.id}
            className={`atab ${days === p.days ? "on" : ""}`}
            onClick={() => setDays(p.days)}>
            {lang === "es" ? p.label_es : p.label_en}
          </button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <label style={{ fontSize: 12, color: "var(--tx3)" }}>
            {lang === "es" ? "Gasto marketing período (RD$)" : "Marketing spend period (DOP)"}
          </label>
          <input
            type="number" min={0} step="50" value={marketingSpend}
            onChange={e => setMarketingSpend(e.target.value)}
            placeholder="0"
            style={{ width: 110, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)" }}
          />
        </div>
      </div>

      {loading && <div style={{ color: "var(--tx3)" }}>{lang === "es" ? "Cargando…" : "Loading…"}</div>}

      {!loading && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            <Kpi label={lang === "es" ? "Ingresos" : "Revenue"} value={fmt$(data.summary.revenue)} />
            <Kpi label={lang === "es" ? "Pedidos" : "Orders"} value={String(data.summary.orders)} />
            <Kpi label="AOV" value={fmt$(data.summary.aov)} />
            <Kpi label={lang === "es" ? "Compradores únicos" : "Unique buyers"} value={String(data.summary.unique_buyers)} />
            <Kpi label="LTV avg" value={fmt$(data.ltv.avg_ltv)} />
            <Kpi label={lang === "es" ? "Pedidos / cliente" : "Orders / buyer"} value={String(data.ltv.avg_orders_per_buyer)} />
            <Kpi label="CAC" value={cac == null ? "—" : fmt$(cac)} hint={lang === "es" ? "spend / compradores" : "spend / buyers"} />
            <Kpi label="LTV / CAC" value={cac == null || !data.ltv.avg_ltv ? "—" : (data.ltv.avg_ltv / cac).toFixed(2) + "×"} />
          </div>

          <Card title={lang === "es" ? "Ingresos diarios" : "Daily revenue"}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 140, overflowX: "auto" }}>
              {data.revenue_daily.map(d => (
                <div key={d.day} title={`${d.day}\n${fmt$(d.revenue)} • ${d.orders} pedidos`}
                  style={{
                    flex: "0 0 6px",
                    height: `${Math.max(2, (Number(d.revenue) / maxRev) * 130)}px`,
                    background: "var(--ac)",
                    borderRadius: 2,
                  }}
                />
              ))}
              {data.revenue_daily.length === 0 && <span style={{ color: "var(--tx3)" }}>{lang === "es" ? "Sin datos" : "No data"}</span>}
            </div>
          </Card>

          <Card title={lang === "es" ? "Top SKUs" : "Top SKUs"}>
            <div style={{ display: "grid", gap: 6 }}>
              {data.top_skus.length === 0 && <span style={{ color: "var(--tx3)" }}>{lang === "es" ? "Sin datos" : "No data"}</span>}
              {data.top_skus.map(s => (
                <div key={s.product_id} style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--bd2)" }}>
                  <span style={{ fontSize: 13 }}>{s.name ?? s.product_id}</span>
                  <span style={{ fontSize: 13, textAlign: "right", color: "var(--tx3)" }}>{s.units} u</span>
                  <span style={{ fontSize: 13, textAlign: "right", fontWeight: 600 }}>{fmt$(s.revenue)}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card title={lang === "es" ? "Cohortes mensuales (revenue)" : "Monthly cohorts (revenue)"}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={th}>{lang === "es" ? "Mes" : "Month"}</th>
                    <th style={th}>{lang === "es" ? "Clientes" : "Users"}</th>
                    {cohortOffsets.map(m => <th key={m} style={th}>M{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.cohorts.length === 0 && (
                    <tr><td colSpan={cohortOffsets.length + 2} style={{ padding: 10, color: "var(--tx3)" }}>
                      {lang === "es" ? "Sin cohortes en el período" : "No cohorts in period"}
                    </td></tr>
                  )}
                  {data.cohorts.map(c => (
                    <tr key={c.cohort_month}>
                      <td style={td}>{String(c.cohort_month).slice(0, 7)}</td>
                      <td style={td}>{c.cohort_size}</td>
                      {cohortOffsets.map(m => {
                        const cell = c.row?.[String(m)];
                        return (
                          <td key={m} style={{ ...td, background: cell ? `hsla(160, 60%, 45%, ${Math.min(0.5, (cell.revenue || 0) / 5000)})` : undefined }}>
                            {cell ? fmt$(cell.revenue) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div style={{ padding: 12, border: "1px solid var(--bd2)", borderRadius: 8, background: "var(--bg2)" }}>
      <div style={{ fontSize: 11, color: "var(--tx3)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: "var(--tx3)", marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: 14, border: "1px solid var(--bd2)", borderRadius: 8, background: "var(--bg2)" }}>
      <h4 style={{ margin: "0 0 10px", fontSize: 14 }}>{title}</h4>
      {children}
    </div>
  );
}

const th: React.CSSProperties = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--bd2)", color: "var(--tx3)", fontWeight: 500 };
const td: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid var(--bd2)", whiteSpace: "nowrap" };